import qs from "qs";
import { assertEvent, assign, emit, fromCallback, fromPromise, setup } from "xstate";

const ACCESS_STATE = "security_state";
export const ACCESS_TOKEN = "access_token";
export const ACCESS_REFRESHTOKEN = "access_refreshtoken";

export const FALLBACK_ORG = "__org__";
export const FALLBACK_PRJ = "__prj__";

function pick<T extends object, K extends Array<keyof T>>(object: T, keys: K): Pick<T, K[number]> {
  const uniqueKeys = [...new Set(keys)];
  return Object.fromEntries(Object.entries(object).filter(([key]) => uniqueKeys.includes(key as K[number]))) as Pick<
    T,
    K[number]
  >;
}

async function parseExchangeTokenResult(response: Response, config: Config): Promise<AuthenticatedUser> {
  if (response.status !== 200) {
    throw {
      code:
        response.status === 404
          ? "RTE"
          : response.status === 403 || response.status === 401
            ? "UNAUTH"
            : response.status,
      allowRefresh: [401, 403].includes(response.status),
    } satisfies ErrorObject;
  }

  const exchangeTokenResult = (await response.json()) as ExchangeTokenResult;

  const roleClaim = exchangeTokenResult.user.claims.find(({ type }) => type === "role");

  if (roleClaim == null) {
    throw { code: "UNAUTH" } satisfies ErrorObject;
  }

  const PERMISSION_NAME_RE = /^can([a-z])/i;
  const userPermissions = qs.parse(
    roleClaim.value
      .split(",")
      .map((permission) => {
        const [app, name, state, org = FALLBACK_ORG, prj = FALLBACK_PRJ] = permission.split("-");
        return `${app.replaceAll("_", " ")}[${name.replace(PERMISSION_NAME_RE, (_, l: string) => `can${l.toUpperCase()}`)}][${org.toLowerCase()}][${prj.toLowerCase()}]=${String(
          state === "true",
        )}`;
      }, {})
      .join("&"),
    {
      decoder: (str, defaultDecoder, _c, type) => {
        if (type === "key") return defaultDecoder(str);
        return str === "true";
      },
    },
  )[config.app] as UserPermission;

  localStorage.setItem(ACCESS_TOKEN, exchangeTokenResult.access_token);
  localStorage.setItem(ACCESS_REFRESHTOKEN, exchangeTokenResult.refresh_token);

  return {
    user: exchangeTokenResult.user,
    permissions: userPermissions,
    token: {
      access_token: exchangeTokenResult.access_token,
      refresh_token: exchangeTokenResult.refresh_token,
      expires_in: Date.now() + exchangeTokenResult.expires_in * 1e3,
    },
  };
}

function logIn(path: Nullable<string>, config: Config) {
  clearCache();

  path ??= getRedirectPath();

  const state = btoa(JSON.stringify({ path, timestamp: new Date() }));

  localStorage.setItem(ACCESS_STATE, state);

  const params = new URLSearchParams({
    state,
    tenant: config.tenant,
    product: config.product,
    client_id: "dummy",
    provider: "saml",
    response_type: "code",
    redirect_uri: `${window.location.origin}/auth`,
  });

  return `${config.authorizeUrl}?${params.toString()}`;
}

function clearCache() {
  localStorage.removeItem(ACCESS_REFRESHTOKEN);
  localStorage.removeItem(ACCESS_STATE);
  localStorage.removeItem(ACCESS_TOKEN);
}

export const AuthMachine = setup({
  types: {
    context: {} as AuthMachineContext,
    events: {} as AuthMachineEvents,
    input: {} as AuthMachineInput,
    emitted: {} as AuthMachineEmits,
  },
  actors: {
    isAuthRoute: fromPromise<boolean, { stateKey: string }>(async () => new Promise(() => void 0)),
    waitForRouter: fromPromise<void>(async () => new Promise(() => void 0)),
    /**
     * Redirect here is best when done client-side, as a full redirect causes the whole auth flow to be redone
     * possibly through a different path, as at this stage the tokens have been stored
     */
    routerRedirect: fromPromise<void, { url: string }>(async () => new Promise(() => void 0)),
    fetchCachedData: fromPromise<NonNullable<CachedData>>(async () => {
      const cache = {
        accessToken: localStorage.getItem(ACCESS_TOKEN),
        refreshToken: localStorage.getItem(ACCESS_REFRESHTOKEN),
      };

      if (!cache?.accessToken || !cache.refreshToken) {
        throw { code: "UNK" } satisfies ErrorObject;
      }

      return await Promise.resolve({
        accessToken: cache.accessToken,
        refreshToken: cache.refreshToken,
      });
    }),
    fetchUserData: fromPromise<AuthenticatedUser, { access_token: Nullable<string>; config: Config }>(
      async ({ input }) => {
        if (input.access_token == null) throw { code: "COD" } satisfies ErrorObject;

        const { app, ssoApiUrl } = input.config;
        const response = await fetch(`${ssoApiUrl}/api/oauth/me/?applicationName=${app}`, {
          headers: { authorization: `Bearer ${input.access_token}` },
        });

        return parseExchangeTokenResult(response, input.config);
      },
    ),
    refreshToken: fromPromise<AuthenticatedUser, { refreshToken: Nullable<string>; config: Config }>(
      async ({ input }) => {
        if (input.refreshToken == null) throw { code: "COD" } satisfies ErrorObject;
        const { app, product, tenant, tokenUrl } = input.config;

        const url = `${tokenUrl}?product=${product}&tenant=${tenant}&applicationName=${app}`;

        const response = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: new URLSearchParams({
            grant_type: "refresh_token",
            refresh_token: input.refreshToken,
            client_id: "dummy",
            client_secret: "dummy",
          }).toString(),
        });

        return parseExchangeTokenResult(response, input.config);
      },
    ),
    exchangeToken: fromPromise<AuthenticatedUser, { code: Nullable<string>; config: Config }>(async ({ input }) => {
      if (input.code == null) throw { code: "COD" } satisfies ErrorObject;
      const { app, product, tenant, tokenUrl } = input.config;

      const response = await fetch(`${tokenUrl}?product=${product}&tenant=${tenant}&applicationName=${app}`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          code: input.code,
          client_id: "dummy",
          tenant,
          product,
          client_secret: "dummy",
          grant_type: "authorization_code",
          redirect_uri: `${window.location.origin}/auth`,
        }).toString(),
      });

      return parseExchangeTokenResult(response, input.config);
    }),
    logInActor: fromPromise<void, { path: string | null; config: Config }>(async ({ input }) => {
      window.location.href = logIn(input.path, input.config);
      await Promise.resolve();
    }),
    externalLogInActor: fromPromise<void, { config: Config }>(async ({ input }) => {
      const EXPECTED_ACCESS_KEYS: Record<string, boolean> = {
        [ACCESS_STATE]: false,
        [ACCESS_TOKEN]: false,
        [ACCESS_REFRESHTOKEN]: false,
      };
      
      let resolve: (value: true) => void;
      let reject: () => void;
      const promise = new Promise<true>((res, rej) => (resolve = res, reject = rej));

      const onStorageEvent = (event: StorageEvent) => {
        if (event.key == null) return;

        // Mark this key as seen
        EXPECTED_ACCESS_KEYS[event.key] = true;

        // Only resolve the promise if all access keys have been seen with data
        if (Object.values(EXPECTED_ACCESS_KEYS).every(Boolean)) resolve(true);
      };

      window.open(logIn("/external-login", input.config));
      window.addEventListener("storage", onStorageEvent);

      // Reject in 10 seconds
      const timeoutId = setTimeout(() => reject(), 10e3);
      await promise.then(() => clearTimeout(timeoutId));

      window.removeEventListener("storage", onStorageEvent);
    }),
    logoutActor: fromPromise<void, { access_token: Nullable<string>; config: Config; everywhere: boolean }>(
      async ({ input }) => {
        if (input.access_token == null) throw { code: "COD" } satisfies ErrorObject;

        const map = { "+": "-", "/": "_" };
        const base64URLUnsafeRegex = new RegExp(`[${Object.keys(map).join("")}]`, "g");
        const base64URLEncodedURL = btoa(input.config.ssoApiUrl).replace(
          base64URLUnsafeRegex,
          (m) => map[m as keyof typeof map] ?? m,
        );

        const response = await fetch(`/api/logout?to=${base64URLEncodedURL}`, {
          method: "POST",
          headers: { authorization: `Bearer ${input.access_token}`, "content-type": "application/json;charset=UTF-8" },
          body: JSON.stringify({ everywhere: input.everywhere }),
        });

        if (response.ok) return;

        console.error(response);
        throw { code: "UNK" } satisfies ErrorObject;
      },
    ),
    validateState: fromPromise(async () => {
      const { code, state } = Object.fromEntries(new URLSearchParams(window.location.search));

      const storedState = localStorage.getItem(ACCESS_STATE);
      localStorage.removeItem(ACCESS_STATE);

      if (storedState == null || storedState !== state) {
        throw { code: "STA" } satisfies ErrorObject;
      }

      return await Promise.resolve({ code, state: storedState });
    }),
    refreshTokenTimerCallback: fromCallback<AuthMachineEvents, Pick<Token, "expires_in">>(({ input, sendBack }) => {
      const buffer = 5e3;
      const timeLeft = input.expires_in - Date.now();

      const callback = () => sendBack({ type: "REFRESH_TOKEN" } satisfies AuthMachineEvents);

      if (timeLeft <= buffer) callback();
      else {
        const timer = setTimeout(callback, timeLeft - buffer);
        return () => clearTimeout(timer);
      }
    }),
  },
  actions: {
    clearCache,
    clearState: assign({
      error: null,
      code: null,
      user: null,
      token: null,
      state: null,
      allowRefresh: null,
      permissions: null,
    }),
    saveError: assign(({ context, event }) => {
      if (!("error" in event)) return {};

      let code: ErrorObject["code"];
      let allowRefresh = false;

      if (event.error instanceof Error) {
        code = "UNK";
        console.error(event.error);
      } else {
        ({ allowRefresh = allowRefresh, code } = event.error as ErrorObject);

        // If this error has been raised previously, prevent another refresh
        if (allowRefresh && context.allowRefresh && context.error === code) allowRefresh = false;
      }

      return { error: code, allowRefresh };
    }),
  },
}).createMachine({
  id: "AuthMachine",
  context: ({ input }) => ({
    config: input.config,
    code: null,
    permissions: null,
    token: null,
    user: null,
    state: null,
    error: null,
    allowRefresh: null,
    tokenRevoked: null,
  }),
  initial: "waitingForRouter",
  states: {
    waitingForRouter: {
      invoke: {
        src: "waitForRouter",
        onDone: { target: "checkIsAuthRoute" },
      },
    },
    checkIsAuthRoute: {
      invoke: {
        src: "isAuthRoute",
        input: { stateKey: ACCESS_STATE },
        onDone: [{ guard: ({ event }) => event.output, target: "validatingState" }, { target: "checkingDataCache" }],
        onError: { target: "#error", actions: "saveError" },
      },
    },
    checkingDataCache: {
      invoke: {
        src: "fetchCachedData",
        onDone: {
          target: "fetchingUserData",
          actions: assign({
            token: ({ event }) => ({
              access_token: event.output.accessToken,
              refresh_token: event.output.refreshToken,
              expires_in: 0,
            }),
          }),
        },
        onError: {
          actions: ({ context }) => {
            window.location.href = logIn(null, context.config);
          },
        },
      },
    },
    fetchingUserData: {
      invoke: {
        src: "fetchUserData",
        input: ({ context }) => ({
          access_token: context.token?.access_token,
          config: context.config,
        }),
        onDone: {
          target: "#authenticated",
          actions: assign(({ event }) => pick(event.output, ["user", "permissions", "token"])),
        },
        onError: { target: "#error", actions: "saveError" },
      },
    },
    validatingState: {
      initial: "validateState",
      states: {
        validateState: {
          invoke: {
            src: "validateState",
            onDone: {
              actions: assign(({ event }) => pick(event.output, ["code", "state"])),
              target: "exchangingToken",
            },
            onError: { target: "#error" },
          },
        },
        exchangingToken: {
          tags: ["AuthComplete"],
          invoke: {
            src: "exchangeToken",
            input: ({ context }) => ({
              code: context.code,
              config: context.config,
            }),
            onDone: {
              target: "redirecting",
              actions: assign(({ event }) => pick(event.output, ["user", "token", "permissions"])),
            },
            onError: { target: "#error", actions: "saveError" },
          },
        },
        redirecting: {
          invoke: {
            src: "routerRedirect",
            input: ({ context }) => {
              const stateObject = JSON.parse(atob(context.state ?? "e30=")) as { path: string };
              return { url: stateObject.path ?? "/" };
            },
            onDone: { target: "#authenticated" },
          },
        },
      },
    },
    refreshingToken: {
      id: "refreshingToken",
      invoke: {
        src: "refreshToken",
        input: ({ context }) => ({
          refreshToken: context.token?.refresh_token,
          config: context.config,
        }),
        onDone: {
          target: "#authenticated",
          actions: assign(({ event }) => pick(event.output, ["user", "token", "permissions"])),
        },
        onError: { target: "#error", actions: "saveError" },
      },
    },
    authenticated: {
      id: "authenticated",
      tags: ["Settled", "AuthComplete"],
      initial: "idle",
      invoke: {
        src: "refreshTokenTimerCallback",
        input: ({ context }) => ({ expires_in: context.token?.expires_in ?? 0 }),
      },
      states: {
        idle: {
          on: {
            LOGOUT: {
              target: "logOut",
            },
            REFRESH_PERMISSIONS: {
              target: "refreshPermissions",
            },
            REFRESH_TOKEN: {
              target: "#refreshingToken",
            },
          },
        },
        logOut: {
          invoke: {
            src: "logoutActor",
            input: ({ context, event }) => {
              assertEvent(event, "LOGOUT");

              return {
                access_token: context.token?.access_token,
                config: context.config,
                everywhere: event.everywhere,
              };
            },
            onDone: { target: "#unauthenticated", actions: assign({ tokenRevoked: true }) },
            onError: { target: "#unauthenticated", actions: assign({ tokenRevoked: false }) },
          },
          exit: [{ type: "clearState" }, { type: "clearCache" }],
        },
        refreshPermissions: {
          invoke: {
            src: "fetchUserData",
            input: ({ context, event }) => {
              assertEvent(event, "REFRESH_PERMISSIONS");

              return {
                config: context.config,
                access_token: context.token?.access_token,
              };
            },
            onDone: {
              target: "#authenticated",
              actions: assign(({ event }) => pick(event.output, ["user", "permissions", "token"])),
            },
            onError: { target: "#error", actions: "saveError" },
          },
        },
      },
    },
    unauthenticated: {
      id: "unauthenticated",
      tags: ["Settled", "AuthIncomplete"],
      entry: emit({ type: "UNAUTHENTICATED" }),
      on: {
        LOGIN: { target: "#login", actions: assign({ tokenRevoked: null }) },
      },
    },
    error: {
      id: "error",
      tags: ["Settled", "AuthIncomplete"],
      initial: "idle",
      states: {
        idle: {
          always: [
            {
              guard: ({ context }) => context.allowRefresh !== true,
              target: "showingError",
            },
            { target: "#refreshingToken" },
          ],
        },
        showingError: {
          on: {
            LOGIN: { target: "#login" },
            EXTERNAL_LOGIN: { target: "#externalLogin" },
          },
        },
      },
    },
    login: {
      id: "login",
      invoke: {
        src: "logInActor",
        input: ({ context, event }) => {
          assertEvent(event, "LOGIN");
          return { path: event.path, config: context.config };
        },
      },
    },
    externalLogin: {
      id: "externalLogin",
      invoke: {
        src: "externalLogInActor",
        input: ({ context }) => ({ config: context.config }),
        onDone: { target: "checkingDataCache" },
        onError: { target: "error" },
      },
    },
  },
});

export const getAuthStateMessage = (isAuthenticated: boolean): AuthMessage => {
  if (isAuthenticated) {
    return {
      main: "You are logged in!",
      content: "To be redirected to the app, please press the button below",
    };
  }

  return {
    main: "You are logged out!",
    content: "To be redirected to log in, please press the Log In button below.",
  };
};

export const getAuthErrorMessage = (code: ErrorObject["code"]): AuthMessage => {
  switch (code) {
    case "UNAUTH":
      return {
        main: "Sorry, there was an error showing you that data.",
        content:
          "You either lack the permissions to see the data, or the data you have requested is invalid. You are welcome to retry.",
        subtitle: "If you think you have reached this screen in error, please raise this issue with your support team.",
      };
    case "RTE":
      return {
        main: "We have been unable to continue your previous session.",
        content: "To return to the last point you visited, please press the Log In button below.",
      };
    case "STA":
      return {
        main: "We cannot validate the state from the authorization server.",
        content: "To re-attempt to log in, please press the Log In button below.",
      };
    default:
      return {
        main: "We encountered an issue while we were trying to sign you in.",
        content: "You can retry the process by pressing the Log In button below.",
        subtitle: "If this message keeps appearing, please raise it with your support team.",
      };
  }
};

export const getRedirectPath = () => {
  return /^\/auth\/?$/i.test(window.location.pathname) ? "/" : window.location.href.replace(window.location.origin, "");
};

interface AuthMessage {
  main: string;
  content: string;
  subtitle?: string;
}

interface AuthMachineContext {
  code: string | null;
  user: User | null;
  token: Token | null;
  permissions: UserPermission | null;
  state: string | null;
  error: ErrorObject["code"] | null;
  tokenRevoked: boolean | null;
  allowRefresh: ErrorObject["allowRefresh"] | null;
  readonly config: Config;
}

type AuthMachineEvents =
  | { type: "VALIDATE_STATE"; code: string; state: string }
  | { type: "LOGIN"; path: string | null }
  | { type: "EXTERNAL_LOGIN" }
  | { type: "LOGOUT"; everywhere: boolean }
  | { type: "REFRESH_PERMISSIONS" }
  | { type: "REFRESH_TOKEN" };

type AuthMachineInput = Pick<AuthMachineContext, "config">;

type AuthMachineEmits = { type: "UNAUTHENTICATED" };

export type UserPermission<T extends string = string> = Partial<
  Record<T, { [Organisation: string]: { [Project: string]: boolean } }>
>;

interface Claim {
  type: string;
  value: string;
  issuer?: string;
  originalIssuer?: string;
  properties?: Record<string, string>;
  subject?: string | null;
  valueType?: string;
}

export interface ExchangeTokenResult {
  refresh_token: string;
  access_token: string;
  /** In seconds */
  expires_in: number;
  user: User;
}

interface Token {
  refresh_token: string;
  access_token: string;
  expires_in: number;
}

export interface ApplicationMosaic {
  name: string;
  created: string;
  embargoDate: string;
  goLiveDate: string;
  enabled: boolean;
  isLive: boolean;
  order: number;
}

export interface User {
  identityProviderId: string;
  isSystemUser: boolean;
  /** @deprecated Prefer the identityProviderId for ID purposes */
  email: string;
  firstName: string;
  lastName: string;
  active: boolean;
  tenant: string;
  product: string;
  claims: Claim[];
  mosaics: ApplicationMosaic[];
}

export interface AuthenticatedUser {
  user: User;
  token: Token;
  permissions: UserPermission;
}

type CachedData = null | {
  accessToken: string;
  refreshToken: string;
};

interface Config {
  product: string;
  tenant: string;
  app: string;
  tokenUrl: string;
  authorizeUrl: string;
  ssoApiUrl: string;
}

interface KNOWN_ERROR_CODES {
  STA: "Invalid State (When the state compared from the storage and URL params do not match)";
  COD: "Missing Code (When actors require a code/token and an invalid value is provided)";
  UNK: "Unknown Error (When the error is unknown and probably unrecoverable)";
  UNAUTH: "Unauthorized";
  RTE: "Refresh Token Expired";
}

export interface ErrorObject {
  code:
    | keyof KNOWN_ERROR_CODES
    // HTTP status error codes
    | number;
  allowRefresh?: boolean;
}

type Nullable<T> = T | null | undefined;
